import axios from 'axios';

const examAxios = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

examAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('examToken') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const slug = localStorage.getItem('tenantSlug');
  if (slug) config.headers['X-Tenant-Slug'] = slug;
  return config;
});

examAxios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('examToken');
      window.location.href = '/examination/login';
    }
    return Promise.reject(err);
  }
);

function slug() {
  return localStorage.getItem('tenantSlug') || '';
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export const getExamDashboard = () =>
  examAxios.get(`/tenant/${slug()}/exam/dashboard`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
export const listExamProjects = (status) =>
  examAxios.get(`/tenant/${slug()}/exam/projects`, { params: { status } }).then((r) => r.data);

export const createExamProject = (data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects`, data).then((r) => r.data);

export const getExamProject = (id) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${id}`).then((r) => r.data);

export const updateExamProject = (id, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${id}`, data).then((r) => r.data);

export const deleteExamProject = (id) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${id}`);

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------
export const listExamSources = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/sources`).then((r) => r.data);

export const uploadExamSource = (projectId, formData) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/sources/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

export const addOerSource = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/sources/oer`, data).then((r) => r.data);

export const deleteExamSource = (projectId, sourceId) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${projectId}/sources/${sourceId}`);

// ---------------------------------------------------------------------------
// Outcomes / Mapping
// ---------------------------------------------------------------------------
export const listExamOutcomes = (projectId, outcomeType) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/outcomes`, {
    params: outcomeType ? { outcome_type: outcomeType } : {}
  }).then((r) => r.data);

export const createExamOutcome = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/outcomes`, data).then((r) => r.data);

export const updateExamOutcome = (projectId, id, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${projectId}/outcomes/${id}`, data).then((r) => r.data);

export const deleteExamOutcome = (projectId, id) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${projectId}/outcomes/${id}`);

export const extractOutcomes = (projectId) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/outcomes/extract`).then((r) => r.data);

export const approveAllOutcomes = (projectId) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/outcomes/approve-all`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Blueprints
// ---------------------------------------------------------------------------
export const listBlueprints = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/blueprints`).then((r) => r.data);

export const createBlueprint = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/blueprints`, data).then((r) => r.data);

export const updateBlueprint = (projectId, id, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${projectId}/blueprints/${id}`, data).then((r) => r.data);

export const deleteBlueprint = (projectId, id) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${projectId}/blueprints/${id}`);

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------
export const listQuestions = (projectId, filters = {}) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/questions`, { params: filters }).then((r) => r.data);

export const createQuestion = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/questions`, data).then((r) => r.data);

export const updateQuestion = (projectId, id, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${projectId}/questions/${id}`, data).then((r) => r.data);

export const generateQuestions = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/questions/generate`, data).then((r) => r.data);

export const deleteQuestion = (projectId, id) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${projectId}/questions/${id}`);

// ---------------------------------------------------------------------------
// Evaluations
// ---------------------------------------------------------------------------
export const listEvaluations = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/evaluations`).then((r) => r.data);

export const createEvaluation = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/evaluations`, data).then((r) => r.data);

export const uploadScripts = (projectId, sessionId, formData) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/upload-scripts`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ).then((r) => r.data);

export const triggerEvaluation = (projectId, sessionId) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/trigger-evaluation`
  ).then((r) => r.data);

// ---------------------------------------------------------------------------
// Question Sets
// ---------------------------------------------------------------------------
export const listQuestionSets = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/question-sets`).then((r) => r.data);

export const createQuestionSet = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/question-sets`, data).then((r) => r.data);

export const autoFillQuestionSet = (projectId, data) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/question-sets/auto-fill`, data).then((r) => r.data);

export const updateQuestionSet = (projectId, id, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${projectId}/question-sets/${id}`, data).then((r) => r.data);

export const deleteQuestionSet = (projectId, id) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${projectId}/question-sets/${id}`);

// ---------------------------------------------------------------------------
// CKB – Concept Knowledge Base
// ---------------------------------------------------------------------------
export const extractConcepts = (projectId) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/ckb/extract`).then((r) => r.data);

export const listConcepts = (projectId, filters = {}) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/ckb/concepts`, { params: filters }).then((r) => r.data);

export const updateConcept = (projectId, conceptId, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${projectId}/ckb/concepts/${conceptId}`, data).then((r) => r.data);

export const deleteConcept = (projectId, conceptId) =>
  examAxios.delete(`/tenant/${slug()}/exam/projects/${projectId}/ckb/concepts/${conceptId}`);

// ---------------------------------------------------------------------------
// Q.Bank Upload Module
// ---------------------------------------------------------------------------
export const listQBankUploads = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/qbank/uploads`).then((r) => r.data);

export const uploadQBankFile = (projectId, formData) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/qbank/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

export const parseQBankUpload = (projectId, uploadId) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/qbank/${uploadId}/parse`).then((r) => r.data);

export const mapQBankUpload = (projectId, uploadId) =>
  examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/qbank/${uploadId}/map`).then((r) => r.data);

export const listQBankQuestions = (projectId, uploadId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/qbank/${uploadId}/questions`).then((r) => r.data);

export const listProjectQuestions = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/qbank/questions`).then((r) => r.data);

export const uploadQBankFromDrive = (projectId, { fileId, fileName, oauthToken }) => {
  const fd = new FormData();
  fd.append('file_id', fileId);
  fd.append('file_name', fileName);
  fd.append('oauth_token', oauthToken);
  return examAxios.post(`/tenant/${slug()}/exam/projects/${projectId}/qbank/upload-from-drive`, fd).then((r) => r.data);
};

export const updateQBankQuestion = (projectId, questionId, data) =>
  examAxios.patch(`/tenant/${slug()}/exam/projects/${projectId}/qbank/questions/${questionId}`, data).then((r) => r.data);

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const exportQuestions = (projectId, { reviewStatus, mode } = {}) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/export`, {
    params: {
      ...(reviewStatus ? { review_status: reviewStatus } : {}),
      ...(mode ? { mode } : {}),
    },
    responseType: 'blob',
  }).then((r) => r.data);

export const exportQuestionsGift = (projectId, reviewStatus) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/export/gift`, {
    params: reviewStatus ? { review_status: reviewStatus } : {},
    responseType: 'blob',
  }).then((r) => r.data);

export const getQuestionSetPreviewData = (projectId, qsId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/question-sets/${qsId}/preview-data`)
    .then((r) => r.data);

export const exportQuestionSet = (projectId, qsId, mode = 'paper') =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/question-sets/${qsId}/export`, {
    params: { mode },
    responseType: 'blob',
  }).then((r) => r.data);

export const exportQuestionSetGift = (projectId, qsId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/question-sets/${qsId}/export/gift`, {
    responseType: 'blob',
  }).then((r) => r.data);

export const updateEvalSession = (projectId, sessionId, data) =>
  examAxios.patch(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}`,
    data,
  ).then((r) => r.data);

export const runAiGrading = (projectId, sessionId, force = false) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/run-ai-grading`,
    null,
    { params: { force } },
  ).then((r) => r.data);

export const returnToClassroom = (projectId, sessionId, accessToken) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/return-to-classroom`,
    { access_token: accessToken },
  ).then((r) => r.data);

export const fetchClassroomResponses = (projectId, sessionId, accessToken) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/fetch-classroom-responses`,
    { access_token: accessToken },
  ).then((r) => r.data);

export const listScripts = (projectId, sessionId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/scripts`)
    .then((r) => r.data);

export const listClassroomCourses = (projectId, accessToken) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/classroom-courses`, {
    params: { access_token: accessToken },
  }).then((r) => r.data);

export const pushToGoogleClassroom = (projectId, qsId, body) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/question-sets/${qsId}/push-to-classroom`,
    body,
  ).then((r) => r.data);

// ---------------------------------------------------------------------------
// Tenant branding (public endpoint)
// ---------------------------------------------------------------------------
export const getTenantBranding = () => {
  const s = slug();
  if (!s) return Promise.resolve(null);
  return examAxios.get(`/public/tenant-branding/${s}`).then((r) => r.data).catch(() => null);
};

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
export const getProjectAnalytics = (projectId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/analytics/summary`).then((r) => r.data);

export const getAttainment = (projectId, sessionId) =>
  examAxios.get(`/tenant/${slug()}/exam/projects/${projectId}/analytics/attainment`, {
    params: { session_id: sessionId },
  }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Moodle Integration
// ---------------------------------------------------------------------------
export const getMoodleConfig = () =>
  examAxios.get(`/tenant/${slug()}/exam/settings/moodle`).then((r) => r.data);

export const saveMoodleConfig = (data) =>
  examAxios.put(`/tenant/${slug()}/exam/settings/moodle`, data).then((r) => r.data);

export const testMoodleConnection = () =>
  examAxios.post(`/tenant/${slug()}/exam/settings/moodle/test-connection`).then((r) => r.data);

export const listMoodleCourses = () =>
  examAxios.get(`/tenant/${slug()}/exam/settings/moodle/courses`).then((r) => r.data);

export const pushToMoodle = (projectId, qsId, body) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/question-sets/${qsId}/push-to-moodle`,
    body,
  ).then((r) => r.data);

export const fetchMoodleResponses = (projectId, sessionId, body) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/fetch-moodle-responses`,
    body,
  ).then((r) => r.data);

export const returnToMoodle = (projectId, sessionId) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/evaluations/${sessionId}/return-to-moodle`,
  ).then((r) => r.data);

// ---------------------------------------------------------------------------
// QBank Approval
// ---------------------------------------------------------------------------
export const submitQBankForApproval = (projectId, data) =>
  examAxios.post(
    `/tenant/${slug()}/exam/projects/${projectId}/qbank/submit-for-approval`,
    data,
  ).then((r) => r.data);

export const listPendingApprovals = () =>
  examAxios.get(`/tenant/${slug()}/exam/faculty/pending-approvals`).then((r) => r.data);

export const approveQBank = (reviewId, notes) =>
  examAxios.post(
    `/tenant/${slug()}/exam/faculty/reviews/${reviewId}/approve`,
    null,
    { params: notes ? { reviewer_notes: notes } : {} },
  ).then((r) => r.data);

export const rejectQBank = (reviewId, notes) =>
  examAxios.post(
    `/tenant/${slug()}/exam/faculty/reviews/${reviewId}/reject`,
    null,
    { params: notes ? { reviewer_notes: notes } : {} },
  ).then((r) => r.data);

// ---------------------------------------------------------------------------
// Faculty (classroom_users) list
// ---------------------------------------------------------------------------
export const listFaculty = () =>
  examAxios.get(`/tenant/${slug()}/exam/faculty`).then((r) => r.data);

// ---------------------------------------------------------------------------
// User Profile
// ---------------------------------------------------------------------------
export const getExamProfile = () =>
  examAxios.get(`/tenant/${slug()}/exam/me`).then((r) => r.data);

export const updateExamProfile = (data) =>
  examAxios.put(`/tenant/${slug()}/exam/me`, data).then((r) => r.data);

export const changeExamPassword = (data) =>
  examAxios.post(`/tenant/${slug()}/exam/me/change-password`, data).then((r) => r.data);
